import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { FaStar, FaUser, FaCheck, FaTrash, FaTimes, FaPen } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, onRatingChange, readonly = false, size = 'md' }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-2xl' : 'text-lg';

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          onClick={() => !readonly && onRatingChange?.(star)}
          className={`${sizeClass} transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <FaStar
            className={
              (hovered || rating) >= star
                ? 'text-amber-400'
                : 'text-gray-300'
            }
          />
        </button>
      ))}
    </div>
  );
};

const ReviewCard = ({ review, onDelete, currentUserId, isAdmin }) => {
  const canDelete = currentUserId === review.user_id || isAdmin;
  const date = new Date(review.created_at).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="border-b border-gray-700 pb-6 last:border-0"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center">
            <FaUser className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">{review.user_name}</span>
              {review.verified_purchase && (
                <Badge className="bg-green-600 text-white text-xs">
                  <FaCheck className="mr-1" /> Compra verificada
                </Badge>
              )}
            </div>
            <span className="text-gray-400 text-sm">{date}</span>
          </div>
        </div>
        {canDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(review.id)}
            className="text-gray-400 hover:text-red-500 hover:bg-red-500/10"
          >
            <FaTrash />
          </Button>
        )}
      </div>
      
      <StarRating rating={review.rating} readonly size="sm" />
      
      {review.title && (
        <h4 className="font-semibold text-white mt-3">{review.title}</h4>
      )}
      
      <p className="text-gray-300 mt-2 leading-relaxed">{review.comment}</p>
    </motion.div>
  );
};

const ProductReviews = ({ productoId, productoName, isAuthenticated, currentUser }) => {
  const [reviews, setReviews] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [productoId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/productos/${productoId}/reviews`);
      setReviews(response.data.reviews || []);
      setTotalReviews(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      toast.error('Por favor escribe un comentario');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/productos/${productoId}/reviews`, {
        producto_id: productoId,
        rating,
        title: title.trim(),
        comment: comment.trim()
      }, { withCredentials: false });

      toast.success('¡Gracias por tu reseña!');
      setShowForm(false);
      setRating(5);
      setTitle('');
      setComment('');
      fetchReviews();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al enviar la reseña';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reseña?')) return;

    try {
      await axios.delete(`${API}/productos/${productoId}/reviews/${reviewId}`, {
        withCredentials: false
      });
      toast.success('Reseña eliminada');
      fetchReviews();
    } catch (error) {
      toast.error('Error al eliminar la reseña');
    }
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <Card className="bg-zinc-900 border-zinc-800 mt-8">
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <CardTitle className="text-2xl text-white flex items-center gap-3">
              Reseñas de Clientes
              <Badge variant="outline" className="text-pink-400 border-pink-400">
                {totalReviews} {totalReviews === 1 ? 'reseña' : 'reseñas'}
              </Badge>
            </CardTitle>
            {totalReviews > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <StarRating rating={Math.round(parseFloat(averageRating))} readonly size="sm" />
                <span className="text-white font-bold">{averageRating}</span>
                <span className="text-gray-400">de 5</span>
              </div>
            )}
          </div>
          
          {isAuthenticated && !showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-pink-600 hover:bg-pink-700"
            >
              <FaPen className="mr-2" /> Escribir Reseña
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Formulario de nueva reseña */}
        <AnimatePresence>
          {showForm && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              onSubmit={handleSubmit}
              className="mb-8 p-6 bg-zinc-800 rounded-xl"
            >
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-white">Tu Reseña para {productoName}</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <FaTimes />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2">Calificación</label>
                  <StarRating rating={rating} onRatingChange={setRating} size="lg" />
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">Título (opcional)</label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ej: Excelente producto"
                    className="bg-zinc-700 border-zinc-600 text-white"
                    maxLength={100}
                  />
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">Tu comentario *</label>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Cuéntanos tu experiencia con este producto..."
                    className="bg-zinc-700 border-zinc-600 text-white min-h-[120px]"
                    maxLength={1000}
                    required
                  />
                  <p className="text-gray-500 text-sm mt-1">{comment.length}/1000</p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={submitting || !comment.trim()}
                    className="bg-pink-600 hover:bg-pink-700"
                  >
                    {submitting ? 'Enviando...' : 'Publicar Reseña'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="border-gray-600 text-gray-300 hover:bg-zinc-700"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Lista de reseñas */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600 mx-auto"></div>
            <p className="text-gray-400 mt-2">Cargando reseñas...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <FaStar className="text-5xl text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Aún no hay reseñas para este producto</p>
            <p className="text-gray-500">¡Sé el primero en dejar tu opinión!</p>
          </div>
        ) : (
          <div className="space-y-6">
            <AnimatePresence>
              {reviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  onDelete={handleDelete}
                  currentUserId={currentUser?.id}
                  isAdmin={currentUser?.role === 'admin'}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
        
        {/* Mensaje para no autenticados */}
        {!isAuthenticated && (
          <div className="mt-6 p-4 bg-zinc-800 rounded-lg text-center">
            <p className="text-gray-300">
              <a href="/login" className="text-pink-400 hover:text-pink-300 underline">
                Inicia sesión
              </a>
              {' '}para dejar tu reseña
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductReviews;
